import type { CityConfig } from "@/pages/city-template/types";

export const sanLuisConfig: CityConfig = {
  city: "San Luis",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "san-luis",
  route: "/san-luis",
  title: "Emergency & Expedited Passport Services San Luis AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in San Luis, AZ — as fast as 24 hours. No San Luis passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 250 miles east. A+ BBB rated. Serving border community families, cross-border workers, and Yuma County agricultural professionals.",
  h1: "Emergency & Expedited Passport Services in San Luis, AZ",
  heroSubtitle: "San Luis sits at Arizona's southwestern corner on the U.S.-Mexico border, about 250 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a cross-border worker commuting daily to San Luis Río Colorado, an agricultural professional in the Yuma growing region, or a border community family with relatives on both sides of the line.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=San%20Luis%20Arizona%20US%20Mexico%20border%20region%20aerial%20view%20with%20the%20Colorado%20River%20valley%20agricultural%20fields%20stretching%20to%20the%20horizon%20under%20dramatic%20sunset%20sky%2C%20border%20town%20community%20with%20desert%20landscape%20and%20distant%20mountains%2C%20warm%20golden%20hour%20lighting%20on%20irrigated%20farmland%2C%20professional%20wide%20angle%20landscape%20photography%2C%20clean%20composition%2C%20no%20people%20visible&width=1800&height=700&seq=sl-hero-001&orientation=landscape",
  heroImageAlt: "San Luis Arizona agricultural border region at golden hour sunset",
  geoPosition: "32.4870;-114.7822",
  geoPlacename: "San Luis, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 250 miles from San Luis — about 4 hours via I-8 and I-10",
  },
  nearbyCitiesZips: [
    { name: "San Luis", zips: "85349, 85336" },
    { name: "Somerton", zips: "85350" },
    { name: "Yuma", zips: "85364, 85365" },
    { name: "Gadsden", zips: "85336" },
    { name: "Fortuna Foothills", zips: "85367" },
    { name: "Wellton", zips: "85356" },
    { name: "Tacna", zips: "85352" },
    { name: "Dateland", zips: "85333" },
    { name: "Winterhaven CA", zips: "92283" },
    { name: "Bard CA", zips: "92222" },
    { name: "Calexico CA", zips: "92231" },
    { name: "El Centro CA", zips: "92243" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 250 miles from San Luis" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Southwest AZ Hub", desc: "Serving San Luis, Somerton, Gadsden, and all Yuma County border communities" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your San Luis case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Yuma AZ", route: "/yuma" },
    { name: "Expedited passport in Phoenix AZ", route: "/phoenix" },
    { name: "Urgent passport in Tucson AZ", route: "/tucson" },
    { name: "Arizona passport hub", route: "/arizona" },
    { name: "Fast passport in Somerton AZ", route: "/somerton" },
    { name: "Emergency passport in Scottsdale AZ", route: "/scottsdale" },
    { name: "Expedited passport in Nogales AZ", route: "/nogales" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves San Luis remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. San Luis is one of Arizona's most remote passport-service communities at 250 miles from Tucson, making courier service essential for border region travelers who cannot afford an 8-hour round trip.",
  fpcOfficeDistance: "Serving all of Yuma County from the border region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout San Luis, Somerton, Gadsden, and the Yuma County border corridor. From cross-border commuters and agricultural industry professionals to families with relatives in both the U.S. and Mexico, we help Southwest Arizona travelers get their passports fast.",
  nearbyCitiesIntro: "We serve San Luis and nearby communities including Somerton, Gadsden, Yuma, Fortuna Foothills, Wellton, and the entire Yuma County border region — from the Colorado River to the Mexican border.",
  howItWorksIntro: "Most San Luis travelers complete everything online and ship documents overnight. At about 250 miles from Tucson along the I-8 corridor, Fast Passport Center provides essential courier turnaround for border region residents — a critical service for one of Arizona's most remote passport communities.",
  mistakesIntro: "San Luis travelers, including cross-border workers commuting daily to Mexico and agricultural professionals, face unique passport challenges specific to border communities. Here are the most common mistakes we see.",
  testimonialsIntro: "San Luis and Yuma County border community travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in San Luis? Here are the most common ones we hear from border community travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "250 miles to Tucson (4-hour drive each way)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No 8-hour round-trip drive to Tucson needed",
        "Ideal for cross-border workers needing passport books",
        "Perfect for agricultural industry international travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from YUM or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "San Luis is 250 miles from Tucson. Don't waste an 8-hour round-trip drive — our couriers deliver to the Western Passport Center for you.",
      bullets: ["Southwest AZ coverage", "24–48 hour turnaround available", "No 8-hour Tucson drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for San Luis adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving San Luis and Yuma County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in San Luis and Yuma County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for San Luis, Somerton, and border community residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Yuma County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for San Luis and border region residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport San Luis AZ", description: "Urgent passport processing for San Luis, AZ travelers via Western Passport Center courier — about 250 miles from San Luis." },
    { name: "Passport Renewal San Luis AZ", description: "Fast passport renewal for San Luis and Yuma County, Arizona residents." },
    { name: "New Passport San Luis AZ", description: "First-time passport application assistance for San Luis and border region residents." },
    { name: "Lost Passport San Luis AZ", description: "Passport replacement for lost or stolen passports for San Luis and Southwest Arizona residents." },
    { name: "Child Passport San Luis AZ", description: "Minor passport services for San Luis and Yuma County families." },
    { name: "Name Change Passport San Luis AZ", description: "Passport name change service for San Luis, AZ residents." },
  ],
  testimonials: [
    {
      name: "Maria Elena G.",
      location: "San Luis, AZ",
      rating: 5,
      text: "I commute daily to San Luis Río Colorado for work. My passport book was expiring and I needed it for a family emergency in Guadalajara. Fast Passport Center handled everything — I didn't have to drive 4 hours each way to Tucson. New passport in 5 days.",
      service: "Expedited Renewal",
    },
    {
      name: "Javier O.",
      location: "Gadsden, AZ",
      rating: 5,
      text: "Agricultural supervisor for a Yuma lettuce operation — got offered a training trip to our corporate office in Salinas, California, then to Mexico. Needed a passport book fast. Fast Passport Center got me one in 4 business days. Couldn't believe how smooth it was.",
      service: "New Passport",
    },
    {
      name: "The Lopez Family",
      location: "San Luis, AZ",
      rating: 5,
      text: "Border family with relatives in Mexicali and Hermosillo. Three kids needed first passports for a family reunion in Mexico City — first time flying internationally. Fast Passport Center processed all three child passports in 7 business days. The DS-11 guidance was incredibly helpful.",
      service: "Child Passports",
    },
    {
      name: "Rosa M.",
      location: "Somerton, AZ",
      rating: 5,
      text: "Lost my passport during a move from San Luis to Somerton. Had a quinceañera to attend in Culiacán in 2 weeks. Fast Passport Center handled the DS-64 lost passport form and got me a replacement in 4 business days. Huge relief — didn't miss my niece's celebration.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "sl-1",
      name: "San Luis Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "874 N Main St",
      city: "San Luis",
      state: "AZ",
      zip: "85349",
      phone: "(928) 627-2034",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "San Luis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for San Luis and border community residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sl-2",
      name: "Somerton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "102 W Main St",
      city: "Somerton",
      state: "AZ",
      zip: "85350",
      phone: "(928) 627-8520",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Somerton — 5 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Somerton, Gadsden, and northern San Luis residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sl-3",
      name: "Yuma Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2222 S 4th Ave",
      city: "Yuma",
      state: "AZ",
      zip: "85364",
      phone: "(928) 343-2796",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "25 miles north in Yuma",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest acceptance facility in Yuma County. Convenient for San Luis residents visiting Yuma.",
      icon: "ri-mail-line",
    },
    {
      id: "sl-4",
      name: "Arizona Western College — San Luis Learning Center",
      type: "Educational Institution Passport Acceptance",
      address: "1340 N 8th Ave",
      city: "San Luis",
      state: "AZ",
      zip: "85349",
      phone: "(928) 627-9563",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM (limited passport hours — call ahead)",
      distance: "San Luis",
      website: "https://www.azwestern.edu/san-luis",
      websiteLabel: "AWC San Luis",
      notes: "Limited passport acceptance services available. Call ahead to confirm hours and availability before visiting.",
      icon: "ri-building-line",
    },
    {
      id: "sl-5",
      name: "Yuma County Library — San Luis Branch",
      type: "Public Library Passport Acceptance",
      address: "775 N 6th Ave",
      city: "San Luis",
      state: "AZ",
      zip: "85349",
      phone: "(928) 627-8344",
      hours: "Tue & Thu: 10:00 AM – 5:00 PM (passport services only)",
      distance: "San Luis",
      website: "https://www.yumalibrary.org/san-luis",
      websiteLabel: "San Luis Library",
      notes: "Limited passport acceptance hours on Tuesdays and Thursdays only. Call ahead to confirm availability.",
      icon: "ri-book-line",
    },
    {
      id: "sl-6",
      name: "Fortuna Foothills Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11681 S Fortuna Rd",
      city: "Yuma",
      state: "AZ",
      zip: "85367",
      phone: "(928) 342-3635",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles northeast in Fortuna Foothills",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern Yuma County and Fortuna Foothills residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "250 miles — 4-hr drive each way, need appointment", fpc: "We drive for you — no 8-hour round trip needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking You Can Drive to Tucson in a Day",
      description: "San Luis is 250 miles from Tucson — a 4-hour drive each way. That is an 8-hour round trip just to reach the Western Passport Center — and you still need an appointment. Our courier service eliminates this entirely. Do not waste a full day driving across Arizona.",
    },
    {
      icon: "ri-passport-line",
      title: "Confusing Passport Cards with Passport Books — Border Community Trap",
      description: "San Luis residents with passport cards can cross into Mexico by land at the San Luis port of entry. But passport cards do not work for air travel — even domestically within Mexico. If you fly from Mexicali to Mexico City, you need a passport book. Many border residents learn this the hard way.",
    },
    {
      icon: "ri-image-line",
      title: "Border Town Photo Quality Problems",
      description: "Passport photo services in border communities can be inconsistent. Passport photos must be exactly 2x2 inches with a white background, no shadows, and neutral expression. Always verify your photos meet U.S. Department of State standards before submitting your application.",
    },
    {
      icon: "ri-file-list-line",
      title: "Agricultural Workers Missing Document Requirements",
      description: "Yuma County agricultural professionals traveling internationally for industry events or training often arrive with incomplete documentation. H-2A visa holders need specific passport and visa documentation. First-time U.S. passport applicants need certified birth certificates — not photocopies.",
    },
    {
      icon: "ri-parent-line",
      title: "Cross-Border Families and Minors Documentation",
      description: "San Luis families with relatives on both sides of the border applying for children's passports must ensure both parents — or one parent with notarized Form DS-3053 — appear at the acceptance facility. This is especially challenging when one parent is in Mexico.",
    },
    {
      icon: "ri-sun-line",
      title: "Summer Heat and Mail Delays in Yuma County",
      description: "San Luis is one of the hottest communities in Arizona, with summer temperatures regularly exceeding 110°F. Standard mail delivery can be unreliable during extreme heat months. Fast Passport Center's overnight courier service with tracking is far more reliable than standard USPS for border communities.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in San Luis, Somerton, or Yuma." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — 250 miles east via I-8." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your San Luis or Yuma County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is San Luis from a passport agency?",
      a: "San Luis is approximately 250 miles from the Western Passport Center in Tucson — about a 4-hour drive each way via I-8 and I-10. That is an 8-hour round trip. San Luis is one of Arizona's most remote communities from the state's only passport agency, making Fast Passport Center's courier service essential for border region travelers.",
    },
    {
      q: "I cross the border daily to work in San Luis Río Colorado — do I need a passport book or just a passport card?",
      a: "For daily land border crossings at the San Luis port of entry, a passport card is sufficient. However, if you ever need to fly within Mexico (for example, from Mexicali to Mexico City) or travel internationally beyond Mexico, you need a full passport book. We recommend San Luis border commuters carry a passport book for maximum travel flexibility.",
    },
    {
      q: "Does San Luis have its own passport acceptance facility?",
      a: "Yes. The San Luis Post Office at 874 N Main Street is a USPS passport acceptance facility. The Arizona Western College San Luis Learning Center and the San Luis Public Library also offer limited passport acceptance hours. For faster processing, pair your acceptance facility visit with Fast Passport Center's courier service.",
    },
    {
      q: "How fast can I get an emergency passport in San Luis?",
      a: "Emergency processing is typically 24–48 hours for San Luis residents. Our couriers deliver directly to the Western Passport Center in Tucson — 250 miles away. This saves you an 8-hour round-trip drive. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Can I fly from Yuma International Airport internationally?",
      a: "Yuma International Airport (YUM) primarily serves domestic routes and military operations. For international commercial flights, San Luis residents typically use Phoenix Sky Harbor International Airport (PHX, about 185 miles northeast) or San Diego International Airport (SAN, about 170 miles west in California). Both require a valid passport book.",
    },
    {
      q: "I'm an agricultural worker in Yuma County — can I apply by mail?",
      a: "If you have never had a U.S. passport before, you must apply in person using Form DS-11 at an acceptance facility like the San Luis Post Office. Adult renewals (Form DS-82) can be done by mail. Fast Passport Center can help with both — the acceptance facility visit takes care of the in-person requirement, and our courier handles the fast turnaround.",
    },
    {
      q: "How do I renew my passport in San Luis?",
      a: "Adult renewals (16 or older, issued within 15 years, same name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks). This is especially valuable for San Luis residents given the 250-mile distance to Tucson.",
    },
    {
      q: "Where can I get passport photos in San Luis?",
      a: "Passport photo options are limited in San Luis. The closest reliable locations are Walgreens at 2800 S 4th Avenue in Yuma (25 miles north) and CVS Pharmacy at 1600 S 4th Avenue in Yuma. UPS Store locations in Yuma also offer passport photos. Always verify your photos meet exact 2x2 inch specifications with a white background.",
    },
    {
      q: "Do you serve Somerton and Gadsden residents too?",
      a: "Yes. We serve all of Yuma County including Somerton, Gadsden, Fortuna Foothills, Wellton, Tacna, and the entire border corridor. Documents ship overnight from any UPS location in the San Luis and Yuma area.",
    },
    {
      q: "What if I have family on the Mexico side and need passports for my children?",
      a: "San Luis border families with children needing first passports must have both parents appear in person — or one parent with notarized Form DS-3053. If one parent is in Mexico, they can get Form DS-3053 notarized at a U.S. consulate and send it to you. Start this process early — it can take 1–2 weeks to coordinate across the border.",
    },
    {
      q: "Can I use my Mexican passport instead of a U.S. passport for international travel?",
      a: "U.S. citizens, including dual citizens, must enter and depart the United States using a valid U.S. passport. Even if you hold Mexican citizenship and have a Mexican passport, you need a valid U.S. passport for international air travel to and from the United States.",
    },
    {
      q: "How does summer heat in San Luis affect passport document shipping?",
      a: "San Luis experiences some of Arizona's hottest temperatures — regularly exceeding 110°F in summer. Standard USPS mail can be unreliable during extreme heat. Fast Passport Center uses UPS overnight courier with full tracking and temperature-controlled handling to ensure your documents arrive safely in Tucson, even during Yuma County's hottest months.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Yuma (4th Ave)", address: "2800 S 4th Ave, Yuma, AZ 85364", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Yuma (4th Ave)", address: "1600 S 4th Ave, Yuma, AZ 85364", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Yuma", address: "2450 S 4th Ave, Ste 107, Yuma, AZ 85364", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
    { name: "Walgreens — Yuma (16th St)", address: "2491 W 16th St, Yuma, AZ 85364", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Yuma", address: "1450 S Yuma Palms Pkwy, Yuma, AZ 85365", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walmart Photo Center — San Luis", address: "1640 N Main St, San Luis, AZ 85349", hours: "Mon–Sun: 8AM–8PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — San Luis", address: "815 N Cesar Chavez Blvd, San Luis, AZ 85349", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for San Luis and border community residents. Right in town — no drive to Yuma needed.", featured: true },
    { name: "AZ MVD — Yuma", address: "2165 E Gila Ridge Rd, Yuma, AZ 85365", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Yuma and surrounding Yuma County residents. About 25 miles from San Luis.", featured: false },
    { name: "AZ MVD — Somerton", address: "451 E Main St, Somerton, AZ 85350", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Somerton, Gadsden, and northern San Luis area residents.", featured: false },
  ],
  airports: [
    {
      code: "YUM",
      name: "Yuma International Airport",
      distance: "25 miles north of San Luis",
      address: "2191 E 32nd St, Yuma, AZ 85365",
      note: "Closest airport to San Luis with American Airlines domestic service to Phoenix and Dallas. Limited to domestic routes — international travelers connect through PHX. Passport required for international connections.",
      flightUrl: "https://www.yumaairport.com/",
      flightLabel: "YUM Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "185 miles northeast of San Luis",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 3 hours via I-8 and I-10.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "170 miles west of San Luis",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "Major California international hub with extensive Pacific and Latin American routes. About 2 hours 45 minutes west via I-8 through Imperial County. Often the best international option for San Luis travelers heading to Mexico and Asia.",
      flightUrl: "https://www.san.org/",
      flightLabel: "SAN Airport",
      featured: false,
    },
  ],
};